import Person from '@mui/icons-material/Person'
import { Avatar, Box, Button, Chip, CircularProgress, ListItemAvatar } from '@mui/material'
import { getImageLink } from 'src/mentor/helpers/utilityFunctions'
import { APPROVAL_DISAPPROVAL_DIALOG_TYPES } from 'src/supportPortal/views/serviceApprovals/hooks/useApprovalDialog'
import {
  APPROVAL_ROLES,
  canManageApprovalRole,
  findApprovalTypeFromApprovalRole,
  isServiceDisabled,
  shouldShowApprovalButton,
} from '../utils/approvalUtils'

// Helper function to extract practitioner specializations
const getPractitionerSpecializations = (practitioner, specializations) => {
  const servicesApproved = practitioner?.teacherUnEditableFields?.servicesApproved || {}
  const specializationIds = new Set()

  // Collect all specialization IDs from all approved services
  Object.values(servicesApproved).forEach((service) => {
    if (service?.specializations && Array.isArray(service.specializations)) {
      service.specializations.forEach((id) => specializationIds.add(id))
    }
  })

  // Map IDs to specialization objects
  const practitionerSpecs = Array.from(specializationIds)
    .map((id) => specializations?.find((spec) => spec.id === id))
    .filter(Boolean) // Remove any undefined values

  return practitionerSpecs
}

export default function ServiceApprovalsList({
  practitioners = [],
  loading = false,
  onOpenApprovalDisapprovalDialog,
  approvalRoles,
  sourceTabId,
  sourceTabLabel,
  specializations = [],
  userRoles = [],
}) {
  if (loading) {
    return (
      <div className="w-100 d-flex align-items-center justify-content-center p-3">
        <CircularProgress />
      </div>
    )
  }

  return (
    <div className="d-flex flex-column align-items-center w-100">
      {practitioners
        ?.sort((a, b) => a.name?.localeCompare(b.name))
        ?.map((practitioner) => (
          <div
            className="d-flex flex-column w-100 align-items-center"
            style={{ position: 'relative' }}
            key={practitioner?.id}
          >
            <Box
              sx={{
                width: '100%',
                bgcolor: 'background.paper',
                display: 'flex',
                alignItems: 'center',
                p: 1,
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              {' '}
              <ListItemAvatar>
                <Avatar
                  src={practitioner?.profilePicId ? getImageLink(practitioner?.profilePicId) : null}
                >
                  <Person />
                </Avatar>
              </ListItemAvatar>
              <div className="d-flex flex-column gap-2">
                <div className="d-flex align-items-center gap-2" style={{ flexWrap: 'wrap' }}>
                  <span>
                    {practitioner?.name} {isServiceDisabled(practitioner, sourceTabId)}
                  </span>
                  {getPractitionerSpecializations(practitioner, specializations).map((spec) => (
                    <Chip
                      key={spec.id}
                      label={spec.name}
                      size="small"
                      sx={{ height: '20px', fontSize: '0.7rem', '& .MuiChip-label': { px: 1, py: 0 } }}
                    />
                  ))}
                </div>
                <div className="d-flex align-items-center gap-1">
                  {APPROVAL_ROLES.map((approvalRole) => {
                    if (!canManageApprovalRole(approvalRole, userRoles)) {
                      return null
                    }
                    if (!shouldShowApprovalButton(practitioner, approvalRole)) {
                      if (approvalRole === sourceTabId) {
                        return (
                          <Button
                            key={approvalRole}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '8px', minWidth: 'unset !important' }}
                            onClick={() =>
                              onOpenApprovalDisapprovalDialog(
                                practitioner,
                                approvalRole,
                                APPROVAL_DISAPPROVAL_DIALOG_TYPES.APPROVE,
                              )
                            }
                          >
                            Re Kyc for{' '}
                            {findApprovalTypeFromApprovalRole(approvalRole, approvalRoles)?.title}
                          </Button>
                        )
                      } else {
                        return null
                      }
                    }

                    return (
                      <Button
                        key={approvalRole}
                        size="small"
                        variant="outlined"
                        color="success"
                        sx={{ fontSize: '8px', minWidth: 'unset !important' }}
                        onClick={() =>
                          onOpenApprovalDisapprovalDialog(
                            practitioner,
                            approvalRole,
                            APPROVAL_DISAPPROVAL_DIALOG_TYPES.APPROVE,
                          )
                        }
                      >
                        Approve as{' '}
                        {findApprovalTypeFromApprovalRole(approvalRole, approvalRoles)?.title}
                      </Button>
                    )
                  })}
                  {!!sourceTabId && (
                    canManageApprovalRole(sourceTabId, userRoles) && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        sx={{ fontSize: '8px', minWidth: 'unset !important' }}
                        onClick={() =>
                          onOpenApprovalDisapprovalDialog(
                            practitioner,
                            sourceTabId,
                            APPROVAL_DISAPPROVAL_DIALOG_TYPES.DISAPPROVE,
                          )
                        }
                      >
                        Disapprove for {sourceTabLabel || ''}
                      </Button>
                    )
                  )}
                </div>
              </div>
            </Box>
            <div
              style={{
                width: '80%',
                borderBottom: '1px solid #0000001f',
                position: 'absolute',
                bottom: '0px',
              }}
            ></div>
          </div>
        ))}
    </div>
  )
}
